// src/components/Dashboard.jsx - UPDATED with NEW SUBSCRIPTION MODEL
import React, { useState, useEffect } from 'react';
import { Check, Clock, ChevronRight, Film, ArrowUpRight, AlertTriangle, Download, X, Eye } from 'lucide-react';
import { StatCard } from './UIComponents';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  LineChart, Line, PieChart, Pie, Cell
} from 'recharts';
import { useLanguage } from '../contexts/LanguageContext';
import { subscribeToJobUpdates, subscribeToUserJobUpdates } from '../services/socketService';
import { api } from '../services/api';
import QueueMonitor from './QueueMonitor'
import Payment from './Payment';

// NEW SUBSCRIPTION PLANS MODEL
const SUBSCRIPTION_PLANS = {
  basic: { price: 12, rendersPerMonth: 8, name: 'Basic' },
  pro: { price: 25, rendersPerMonth: 20, name: 'Pro' },
  studio: { price: 50, rendersPerMonth: 35, name: 'Studio' }
};

// Sample render data visualization
const renderTrendData = [
  { name: 'Jan', count: 5 },
  { name: 'Feb', count: 8 },
  { name: 'Mar', count: 12 },
  { name: 'Apr', count: 9 },
  { name: 'May', count: 15 },
  { name: 'Jun', count: 18 },
];

const typeData = [
  { name: 'Historical', value: 40 },
  { name: 'Scientific', value: 30 },
  { name: 'Data Viz', value: 20 },
  { name: 'Other', value: 10 },
];

const COLORS = ['#ef4444', '#3b82f6', '#10b981', '#8b5cf6'];

const Dashboard = ({ renderQueue, isLoading, user, setActiveTab }) => {
    const { t } = useLanguage();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [systemStatus, setSystemStatus] = useState('operational');
  const [renderStats, setRenderStats] = useState({
    available: 0,
    total: 0,
    inQueue: 0,
    completed: 0
  });
  const [showPayment, setShowPayment] = useState(false);

  // Load initial jobs
  useEffect(() => {
    const loadJobs = async () => {
      try {
        const response = await api.getRenderQueue();
        setJobs(response.data);
      } catch (error) {
        console.error('Failed to load jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    loadJobs();
  }, []);

  // Subscribe to real-time updates for ALL user jobs
  useEffect(() => {
    if (user?.id) {
      console.log('🔄 Subscribing to real-time job updates for user:', user.id);
      
      const unsubscribe = subscribeToUserJobUpdates(user.id, (updatedJob) => {
        console.log('📡 Received job update:', updatedJob);
        
        setJobs(prevJobs => 
          prevJobs.map(job => 
            job._id === updatedJob._id ? { ...job, ...updatedJob } : job
          )
        );
      });

      return unsubscribe;
    }
  }, [user?.id]);

const getRenderAllocationForPlan = (plan) => {
    const allocations = {
      'Free': 3,
      'Basic': 8,
      'Pro': 20,
      'Studio': 35
    };
    return allocations[plan] || 3;
  };

  useEffect(() => {
    const fetchUserStats = async () => {
      try {
        const response = await api.getCurrentUser();
        if (response?.success && response.data) {
          const userData = response.data;
          
          // ✅ Use the usage stats from the API response
          const usage = userData.usage || userData.user?.getUsageStats?.() || {};
          
          console.log('📊 User data received:', userData);
          console.log('📊 Usage stats:', usage);
          
          setRenderStats(prev => ({
            ...prev,
            available: userData.rendersAvailable || usage.rendersAvailable || 0,
            total: usage.rendersTotal || getRenderAllocationForPlan(userData.subscriptionPlan) || 0
          }));
        }
      } catch (error) {
        console.error('❌ Failed to fetch user stats:', error);
      }
    };

    if (user?.id) {
      fetchUserStats();
    }
  }, [user]);

  // Fetch current user profile + usage on mount
  useEffect(() => {
    const fetchProfileUsage = async () => {
      try {
        const res = await api.getUserProfile();
        if (res?.success && res.data) {
          const { usage } = res.data;
          if (usage) {
            setRenderStats(prev => ({
              ...prev,
              available: usage.rendersAvailable ?? prev.available,
              total: usage.rendersTotal ?? prev.total
            }));
          } else if (res.data.user) {
            const u = res.data.user;
            setRenderStats(prev => ({
              ...prev,
              available: u.rendersAvailable ?? prev.available,
              total: u.rendersTotal ?? prev.total
            }));
          }
        }
      } catch (err) {
        console.error('Failed to fetch user profile/usage:', err);
      }
    };

    fetchProfileUsage();
  }, []);

  // Update stats from jobs (queue/completed only)
   useEffect(() => {
    setRenderStats(prev => ({
      ...prev,
      inQueue: jobs.filter(r => r.status === 'queued').length,
      completed: jobs.filter(r => r.status === 'completed').length
    }));
  }, [jobs]);

  // Function to display welcome message based on time of day
  const getWelcomeMessage = () => {
    const hour = new Date().getHours();
    if (hour < 12) return t('goodMorning', 'Good morning');
    if (hour < 18) return t('goodAfternoon', 'Good afternoon');
    return t('goodEvening', 'Good evening');
  };

   const getNextResetDate = () => {
    if (user?.rendersReset) {
      const resetDate = new Date(user.rendersReset);
      const now = new Date();
      
      if (resetDate > now) {
        const diffTime = resetDate - now;
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
      }
    }
    
    // Fallback: calculate next month's first day
    const now = new Date();
    const nextMonth = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    const diffTime = nextMonth - now;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return `${diffDays} day${diffDays !== 1 ? 's' : ''}`;
  };

  // Handle payment success
  const handlePaymentSuccess = () => {
    setShowPayment(false);
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  // Get user's current plan
  const getCurrentPlan = () => {
    const userPlan = user?.subscriptionPlan?.toLowerCase() || 'free';
    return userPlan === 'free' ? null : SUBSCRIPTION_PLANS[userPlan];
  };

  // Get next plan recommendation
  const getRecommendedUpgrade = () => {
    const userPlan = user?.subscriptionPlan?.toLowerCase() || 'free';
    
    if (userPlan === 'free' || userPlan === 'basic') {
      return SUBSCRIPTION_PLANS.pro;
    } else if (userPlan === 'pro') {
      return SUBSCRIPTION_PLANS.studio;
    }
    return null; // User already has Studio plan
  };

  // Check if user needs an upgrade
  const shouldShowUpgrade = () => {
    const available = renderStats.available || 0;
    const total = renderStats.total || 0;
    const usagePercent = total > 0 ? ((total - available) / total) * 100 : 0;
    
    return usagePercent > 70 || available <= 2; // Show when 70% used or 2 or fewer renders left
  };

  // Custom tooltip for charts
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-gray-800 p-2 rounded-md border border-gray-700 text-sm">
          <p className="text-gray-200">{`${label}: ${payload[0].value}`}</p>
        </div>
      );
    }
    return null;
  };

  // Render pie chart label
  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent, index }) => {
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);

    return (
      <text 
        x={x} 
        y={y} 
        fill="#fff" 
        textAnchor={x > cx ? 'start' : 'end'} 
        dominantBaseline="central"
        fontSize={12}
      >
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  // Show payment modal if active
  if (showPayment) {
    const recommendedPlan = getRecommendedUpgrade();
    return (
      <Payment
        planId={recommendedPlan?.name?.toLowerCase()}
        planName={recommendedPlan?.name}
        amount={recommendedPlan?.price}
        userEmail={user?.email}
        onSuccess={handlePaymentSuccess}
        onCancel={() => setShowPayment(false)}
      />
    );
  }

  return (
    <div className="p-4 md:p-8">
      {/* Welcome Section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center">
        <div>
          <h1 className="text-xl md:text-2xl font-bold text-white">{getWelcomeMessage()}, {user?.firstName || 'User'}!</h1>
          <p className="text-gray-400 mt-1">{t('dashboardSubtitle', 'Here\'s the status of your documentary rendering projects')}</p>
        </div>
        
        {/* System Status Indicator */}
        <div className={`mt-4 md:mt-0 flex items-center px-3 py-1 rounded-full text-sm
                        ${systemStatus === 'operational' ? 'bg-green-900/30 text-green-400' : 
                          systemStatus === 'degraded' ? 'bg-yellow-900/30 text-yellow-400' : 
                          'bg-red-900/30 text-red-400'}`}>
          <div className={`w-2 h-2 rounded-full mr-2 
                         ${systemStatus === 'operational' ? 'bg-green-400' : 
                           systemStatus === 'degraded' ? 'bg-yellow-400' : 
                           'bg-red-400'}`}></div>
          <span>
            {systemStatus === 'operational' ? t('allSystemsOperational') : 
 systemStatus === 'degraded' ? t('systemPerformanceDegraded') : 
 t('systemIssuesDetected')}
          </span>
        </div>
      </div>
      
      {/* Stats Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 mt-6">
    <StatCard 
      title={t('rendersAvailable', 'Renders Available')} 
      value={`${renderStats.available}/${renderStats.total}`} 
      icon={<Film size={24} className="text-red-600" />} 
    />
    <StatCard 
      title={t('inQueue', 'In Queue')}
      value={renderStats.inQueue.toString()} 
      icon={<Clock size={24} className="text-red-600" />} 
    />
    <StatCard 
      title={t('completed', 'Completed')} 
      value={renderStats.completed.toString()} 
      icon={<Check size={24} className="text-red-600" />} 
    />
    <StatCard 
      title={t('nextReset','Next Reset')} 
      value={getNextResetDate()} 
      icon={<ArrowUpRight size={24} className="text-red-600" />} 
    />
  </div>
      
      {/* Upgrade Recommendation - NEW IMPROVED VERSION */}
      {shouldShowUpgrade() && getRecommendedUpgrade() && (
        <div className="mt-8 bg-gradient-to-r from-purple-800 to-purple-600 rounded-lg shadow overflow-hidden">
          <div className="p-6 md:flex items-center justify-between">
            <div className="text-white">
              <div className="flex items-center mb-2">
                <h3 className="font-semibold text-lg mr-2">Upgrade Recommended</h3>
                <span className="bg-yellow-400 text-purple-900 px-2 py-1 rounded-full text-xs font-bold">
                  {renderStats.available <= 2 ? 'LOW RENDERS' : 'USAGE HIGH'}
                </span>
              </div>
              <p className="text-purple-100 max-w-xl mb-3">
                You're running low on renders! Upgrade to <strong>{getRecommendedUpgrade().name} Plan</strong> and get 
                <strong> {getRecommendedUpgrade().rendersPerMonth} renders per month</strong> for just 
                <strong> ${getRecommendedUpgrade().price}/month</strong>.
              </p>
              <div className="flex items-center space-x-4 text-sm">
                <span className="inline-block bg-black/30 px-2 py-1 rounded">
                  {getRecommendedUpgrade().rendersPerMonth} renders/month
                </span>
                <span className="inline-block bg-black/30 px-2 py-1 rounded">4K Resolution</span>
                <span className="inline-block bg-black/30 px-2 py-1 rounded">Priority Queue</span>
                <span className="text-purple-200 font-medium">
                  Only ${(getRecommendedUpgrade().price / getRecommendedUpgrade().rendersPerMonth).toFixed(2)} per render!
                </span>
              </div>
            </div>
            <button 
              onClick={() => setShowPayment(true)}
              className="mt-4 md:mt-0 bg-white text-purple-600 border-2 border-white px-6 py-3 rounded-lg font-bold text-sm hover:bg-purple-50 transition-colors"
            >
              Upgrade Now
            </button>
          </div>
        </div>
      )}

      {/* Current Plan Display */}
      {getCurrentPlan() && (
        <div className="mt-8 bg-gray-800 rounded-lg p-6">
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-white font-semibold text-lg">Current Plan: {getCurrentPlan().name}</h3>
              <p className="text-gray-400">
                ${getCurrentPlan().price}/month • {getCurrentPlan().rendersPerMonth} renders • 
                ${(getCurrentPlan().price / getCurrentPlan().rendersPerMonth).toFixed(2)} per render
              </p>
            </div>
            <button 
              onClick={() => setActiveTab('settings')}
              className="text-blue-400 hover:text-blue-300 text-sm font-medium"
            >
              Manage Plan →
            </button>
          </div>
        </div>
      )}
      
      {/* Data Visualization Section */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
        {/* Monthly Trend Chart */}
        <div className="bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-white mb-4">{t('monthlyRenders','Monthly Renders')}</h2>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart
                data={renderTrendData}
                margin={{ top: 5, right: 30, left: 20, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                <XAxis dataKey="name" stroke="#9CA3AF" />
                <YAxis stroke="#9CA3AF" />
                <Tooltip content={<CustomTooltip />} />
                <Line 
                  type="monotone" 
                  dataKey="count" 
                  stroke="#EF4444" 
                  strokeWidth={2} 
                  dot={{ r: 4, fill: '#EF4444' }}
                  activeDot={{ r: 6 }} 
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
        
        {/* Project Types Chart */}
        <div className="bg-gray-800 rounded-lg shadow p-6">
          <h2 className="text-lg font-semibold text-white mb-4">{t('renderTypes','Render Types')}</h2>
          <div className="h-64 flex items-center justify-center">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={typeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={renderCustomizedLabel}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {typeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip content={<CustomTooltip />} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div className="grid grid-cols-2 mt-4 gap-2">
            {typeData.map((entry, index) => (
              <div key={index} className="flex items-center">
                <div 
                  className="w-3 h-3 rounded-full mr-2" 
                  style={{ backgroundColor: COLORS[index % COLORS.length] }}
                ></div>
                <span className="text-sm text-gray-300">{entry.name}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
      
      {/* Recent Renders */}
      <div className="mt-8">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-white">{t('recentRenders','Recent Renders')}</h2>
          <button 
            onClick={() => setActiveTab('renders')}
            className="text-red-500 hover:text-red-400 text-sm flex items-center"
          >
            View All <ChevronRight size={16} />
          </button>
        </div>
        
        <div className="bg-gray-800 rounded-lg shadow mt-4">
          {loading ? (
            <div className="p-6 text-center">
              <div className="animate-pulse flex justify-center">
                <div className="h-4 w-4 bg-red-600 rounded-full mx-1"></div>
                <div className="h-4 w-4 bg-red-600 rounded-full mx-1 animate-pulse delay-150"></div>
                <div className="h-4 w-4 bg-red-600 rounded-full mx-1 animate-pulse delay-300"></div>
              </div>
              <p className="text-gray-400 mt-2">{t('loadingRenders','Loading renders...')}</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="p-6 text-center">
              <p className="text-gray-400">{t('noRecentRenders','No recent renders found')}</p>
            </div>
          ) : (
            <div className="grid gap-4 p-6">
              {jobs
                .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
                .slice(0, 5)
                .map((job) => (
                  <JobCard key={job._id} job={job} />
                ))}
            </div>
          )}
        </div>
      </div>

      {/* Queue Status Section */}
      <div className="mt-8">
        <QueueMonitor />
      </div>
      
      {/* Free Plan Upgrade Banner */}
      {(!user?.subscriptionPlan || user?.subscriptionPlan === 'Free') && (
  <div className="mt-8 bg-gradient-to-r from-red-800 to-red-600 rounded-lg shadow overflow-hidden">
    <div className="p-6 md:flex items-center justify-between">
      <div className="text-white">
        <h3 className="font-semibold text-lg">{t('startYourJourneyBasic')}</h3>
        <p className="mt-2 text-gray-200 max-w-xl">
          {t('getStartedWith8Renders')} <strong>{t('rendersPerMonthStrong')}</strong> {t('forJust')} <strong>$12</strong>. 
          {t('upgradeToProStudio')}
        </p>
        <div className="mt-3 text-sm space-x-2">
          <span className="inline-block bg-black/30 px-2 py-1 rounded">{t('hdResolution')}</span>
          <span className="inline-block bg-black/30 px-2 py-1 rounded">{t('standardQueue')}</span>
          <span className="inline-block bg-black/30 px-2 py-1 rounded">{t('emailSupport')}</span>
        </div>
      </div>
      <button 
        onClick={() => setActiveTab('settings')} 
        className="mt-4 md:mt-0 bg-black text-white border border-red-500 px-4 py-2 rounded font-medium text-sm hover:bg-gray-900 transition-colors"
      >
        {t('choosePlan')}
      </button>
    </div>
  </div>
)}
      
      {/* System Status Notification - Only show if there are issues */}
      {systemStatus !== 'operational' && (
        <div className="mt-8 bg-yellow-900/20 border border-yellow-800 rounded-lg p-4 flex items-start">
          <div className="mt-0.5">
            <AlertTriangle size={20} className="text-yellow-500" />
          </div>
          <div className="ml-3">
            <h3 className="text-yellow-500 font-medium">{t('systemPerformanceNotice','System Performance Notice')}</h3>
            <p className="text-gray-300 text-sm mt-1">
             {t('higherThanNormalLoad','We\'re experiencing higher than normal load on our render farm. Your renders might take slightly longer than usual. We\'re working on expanding capacity.')} 
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

// Individual Job Card Component (unchanged from original)
const JobCard = ({ job }) => {
  const [localProgress, setLocalProgress] = useState(job.progress || 0);
  const [showPreview, setShowPreview] = useState(false);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    if (job._id) {
      const unsubscribe = subscribeToJobUpdates(job._id, (updatedJob) => {
        setLocalProgress(updatedJob.progress || 0);
      });

      return () => {
        if (typeof unsubscribe === 'function') {
          unsubscribe();
        }
      };
    }
  }, [job._id]);

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      console.log('🔽 Starting download for job:', job._id);
      
      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/renders/${job._id}/download`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Accept': 'application/json, video/mp4, */*'
        },
        credentials: 'include'
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `Download failed: ${response.status}`);
      }

      const contentType = response.headers.get('content-type');
      
      if (contentType && contentType.includes('application/json')) {
        const data = await response.json();
        console.log('📄 Got signed URL response:', data);
        
        if (data.success && data.data && data.data.downloadUrl) {
          const link = document.createElement('a');
          link.href = data.data.downloadUrl;
          link.download = data.data.filename || `${job.name.replace(/[^a-zA-Z0-9]/g, '_')}_render.mp4`;
          link.target = '_blank';
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
          
          console.log('✅ Download triggered via signed URL');
        } else {
          throw new Error('Invalid download response format');
        }
      } else {
        console.log('📁 Handling direct file download');
        const blob = await response.blob();
        
        const contentDisposition = response.headers.get('content-disposition');
        let filename = `${job.name.replace(/[^a-zA-Z0-9]/g, '_')}_render.mp4`;
        
        if (contentDisposition) {
          const matches = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/.exec(contentDisposition);
          if (matches != null && matches[1]) {
            filename = matches[1].replace(/['"]/g, '');
          }
        }
        
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = filename;
        document.body.appendChild(link);
        link.click();
        
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
        
        console.log('✅ Direct download completed');
      }
    } catch (error) {
      console.error('❌ Download failed:', error);
      
      let userMessage = 'Download failed. Please try again.';
      if (error.message.includes('not found')) {
        userMessage = 'Render file not found. It may have expired.';
      } else if (error.message.includes('not complete')) {
        userMessage = 'Render is not complete yet. Please wait.';
      } else if (error.message.includes('not authorized')) {
        userMessage = 'You are not authorized to download this file.';
      }
      
      alert(userMessage);
    } finally {
      setIsDownloading(false);
    }
  };

  const handlePreview = async () => {
    if (showPreview && previewUrl) {
      setShowPreview(false);
      return;
    }

    try {
      console.log('👁️ Loading preview for job:', job._id);
      
      if (job.status === 'completed' && job.outputFilePath) {
        const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:5001/api'}/renders/${job._id}/download`, {
          method: 'GET',
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('token')}`,
            'Accept': 'application/json'
          }
        });

        if (response.ok) {
          const blob = await response.blob();
          const blobUrl = URL.createObjectURL(blob);
          setPreviewUrl(blobUrl);
          setShowPreview(true);
          
          setTimeout(() => {
            URL.revokeObjectURL(blobUrl);
          }, 5 * 60 * 1000);
          return;
        }
      }
      
      if (job.previewImagePath) {
        const previewResponse = await api.getRenderPreview(job._id);
        if (previewResponse.success && previewResponse.data.previewUrl) {
          setPreviewUrl(previewResponse.data.previewUrl);
          setShowPreview(true);
          return;
        }
      }
      
      alert('Preview not available');
      
    } catch (error) {
      console.error('❌ Preview failed:', error);
      alert('Preview failed to load');
    }
  };

  const handleCancel = async () => {
    if (!window.confirm('Are you sure you want to cancel this render?')) return;
    
    try {
      await api.updateRenderJob(job._id, { status: 'cancelled' });
    } catch (error) {
      console.error('Cancel failed:', error);
      alert('Failed to cancel job.');
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'queued': return 'bg-yellow-100 text-yellow-800';
      case 'rendering': return 'bg-blue-100 text-blue-800';
      case 'completed': return 'bg-green-100 text-green-800';
      case 'failed': return 'bg-red-100 text-red-800';
      case 'cancelled': return 'bg-gray-100 text-gray-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityBadge = (priority) => {
    const colors = {
      'Express': 'bg-purple-100 text-purple-800',
      'Priority': 'bg-orange-100 text-orange-800',
      'Standard': 'bg-blue-100 text-blue-800',
      'Economy': 'bg-green-100 text-green-800'
    };
    return colors[priority] || colors['Standard'];
  };

 return (
  <div className="bg-gray-800 rounded-lg shadow-md p-6 border border-gray-700 job-card">
    <div className="flex justify-between items-start mb-4">
      <div>
        <h3 className="text-lg font-semibold text-white">{job.name}</h3>
        <p className="text-sm text-gray-400">
          {job.resolution} • {job.quality} • {job.duration}s
        </p>
      </div>
      <div className="flex space-x-2">
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getPriorityBadge(job.priority)}`}>
          {job.priority}
        </span>
        <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(job.status)}`}>
          {job.status}
        </span>
      </div>
    </div>

    {/* ✅ ANIMATED Progress Bar */}
    {(job.status === 'queued' || job.status === 'rendering') && (
      <div className="mb-4">
        <div className="flex justify-between text-sm text-gray-300 mb-2">
          <span className="status-indicator">
            {job.status === 'queued' ? (
              <>
                <div className="status-dot queued"></div>
                <span className="text-blue-400 font-medium">In Queue</span>
              </>
            ) : (
              <>
                <div className="status-dot rendering"></div>
                <span className="text-yellow-400 font-medium text-glow-yellow">🎬 Rendering</span>
              </>
            )}
          </span>
          <span className="font-bold text-white">{localProgress}%</span>
        </div>
        
        {/* Progress Container with CSS Classes */}
        <div className="progress-container" style={{ height: '12px' }}>
          <div 
            className={`progress-fill h-full ${
              job.status === 'rendering'
                ? 'progress-bar-rendering'
                : job.status === 'queued'
                ? 'progress-bar-queued'
                : 'bg-red-500'
            }`}
            style={{ 
              width: `${Math.max(3, localProgress)}%`,
              minWidth: job.status === 'rendering' ? '12px' : '8px'
            }}
          >
          </div>
          
          {/* Progress text for high progress */}
          {localProgress > 70 && job.status === 'rendering' && (
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-bold text-gray-900" style={{ textShadow: '0 1px 2px rgba(0,0,0,0.5)' }}>
                {localProgress}%
              </span>
            </div>
          )}
        </div>
        
        {/* Status Messages */}
        <div className="mt-2 flex items-center justify-between">
          <div className="flex items-center text-xs">
            {job.status === 'queued' && (
              <div className="flex items-center text-blue-400">
                <div className="w-1 h-1 bg-blue-400 rounded-full mr-2 animate-ping"></div>
                <span>Waiting in {job.priority.toLowerCase()} queue...</span>
              </div>
            )}
            {job.status === 'rendering' && (
              <div className="flex items-center text-yellow-400">
                <div className="w-1 h-1 bg-yellow-400 rounded-full mr-2 animate-ping"></div>
                <span>
                  Frame {Math.floor((localProgress / 100) * job.duration * 24)}/{job.duration * 24}
                </span>
              </div>
            )}
          </div>
          
          {/* Time remaining */}
          {job.status === 'rendering' && localProgress > 5 && (
            <div className="text-xs text-gray-400">
              {(() => {
                const estimated = Math.round(((100 - localProgress) / Math.max(localProgress, 1)) * 2);
                return estimated > 60 
                  ? `~${Math.round(estimated / 60)}h left`
                  : `~${estimated}m left`;
              })()}
            </div>
          )}
        </div>
      </div>
    )}

    {/* Error Message for Failed Jobs */}
    {job.status === 'failed' && job.renderLogs && job.renderLogs.length > 0 && (
      <div className="mb-4 p-3 bg-red-900/20 border border-red-700 rounded-lg">
        <div className="flex items-center mb-2">
          <div className="status-dot failed mr-2"></div>
          <AlertTriangle size={16} className="text-red-400 mr-2" />
          <span className="text-red-400 font-medium text-sm">Render Failed</span>
        </div>
        <p className="text-sm text-red-300">
          {job.renderLogs[job.renderLogs.length - 1]?.message || 'Unknown error occurred'}
        </p>
      </div>
    )}

    {/* Preview Section */}
    {showPreview && previewUrl && (
      <div className="mb-4 p-3 bg-gray-700 rounded-lg">
        <div className="flex items-center justify-between mb-2">
          <span className="text-sm font-medium text-white">Preview</span>
          <button
            onClick={() => setShowPreview(false)}
            className="text-gray-400 hover:text-white transition-colors btn-press"
          >
            <X size={16} />
          </button>
        </div>
        {job.previewImagePath ? (
          <img 
            src={previewUrl} 
            alt="Render preview" 
            className="w-full h-32 object-cover rounded"
            onError={() => {
              console.error('Preview image failed to load');
              setShowPreview(false);
            }}
          />
        ) : (
          <video 
            src={previewUrl} 
            controls 
            className="w-full h-32 rounded"
            onError={() => {
              console.error('Preview video failed to load');
              setShowPreview(false);
            }}
          >
            Your browser does not support video preview.
          </video>
        )}
      </div>
    )}

    {/* Action Buttons */}
    <div className="flex justify-between items-center">
      <div className="text-sm text-gray-500">
        Created: {new Date(job.createdAt).toLocaleDateString()}
      </div>
      <div className="flex space-x-2">
        {job.status === 'completed' && (
          <>
            <button
              onClick={handlePreview}
              className="px-3 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors flex items-center text-sm btn-press"
              title="Preview render"
            >
              <Eye size={16} className="mr-1" />
              {showPreview ? 'Hide' : 'Preview'}
            </button>
            <button
              onClick={handleDownload}
              disabled={isDownloading}
              className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors flex items-center text-sm disabled:opacity-50 disabled:cursor-not-allowed btn-press"
            >
              {isDownloading ? (
                <>
                  <div className="spinner-gold h-4 w-4 border-2 border-t-transparent rounded-full mr-2" />
                  Downloading...
                </>
              ) : (
                <>
                  <Download size={16} className="mr-1" />
                  Download
                </>
              )}
            </button>
          </>
        )}
        {(job.status === 'queued' || job.status === 'rendering') && (
          <button
            onClick={handleCancel}
            className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors flex items-center text-sm btn-press"
          >
            <X size={16} className="mr-1" />
            Cancel
          </button>
        )}
      </div>
    </div>

    {/* Recent Logs */}
    {job.renderLogs && job.renderLogs.length > 0 && job.status !== 'failed' && (
      <div className="mt-4 pt-4 border-t border-gray-700">
        <p className="text-xs text-gray-500 mb-2">Latest Update:</p>
        <p className="text-sm text-gray-300">
          {job.renderLogs[job.renderLogs.length - 1]?.message}
        </p>
      </div>
    )}
  </div>
);
};

export default Dashboard;